import React, { useState } from "react";
import { Check, X, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MONTHS_IT } from "@/lib/constants";
import { formatCounterHours } from "@/lib/counterHours";

const LABELS = { ferie: "Ferie", rol: "ROL / Permessi", ex_fest: "Ex festività" };
const TONE = { blocking: "bg-red-50 text-red-800 border-red-200", warning: "bg-amber-50 text-amber-800 border-amber-200", info: "bg-sky-50 text-sky-800 border-sky-200" };
const SEVERITY = { blocking: "Bloccante", warning: "Attenzione", info: "Info" };
const COMPONENTS = { year_opening: "Saldo inizio anno", accrued_ytd: "Maturato", used_ytd: "Goduto", other_ytd: "Conguagli banca ore", adj_ytd: "Rettifiche" };

const fmt = (c, v) => (c.unit === "days" ? `${Number(v).toFixed(3)} gg` : `${formatCounterHours(v)} h`);

const Details = ({ b }) => (
  <div className="grid md:grid-cols-2 gap-4 px-4 py-3 bg-suite-neutral-50 text-xs" data-testid={`baseline-details-${b.id}`}>
    <div>
      <div className="font-semibold mb-1">Composizione (ore interne)</div>
      {Object.entries(b.counters).map(([k, c]) => (
        <div key={k} className="mb-1.5">
          <span className="font-medium">{LABELS[k]}:</span>{" "}
          {Object.entries(COMPONENTS).map(([ck, cl]) => `${cl} ${formatCounterHours(c.components_hours[ck] || 0)}`).join(" · ")}
        </div>
      ))}
    </div>
    <div>
      <div className="font-semibold mb-1">Provenienza dei dati</div>
      <div>Residui iniziali: {Object.entries(b.sources.initial_residuals).map(([k, v]) => `${k.replace("ratei_", "")}=${v ?? "—"}`).join(", ")}</div>
      <div>Decorrenze storicizzate: {b.sources.accrual_profiles} · Ore/giorno: {b.sources.hours_per_day}</div>
      <div>Godimenti: {b.sources.consumption}</div>
      <div>Altri movimenti: {b.sources.other_movements}</div>
      <div>Motore: {b.sources.engine} · Impronta: <span className="font-mono">{b.inputs_hash.slice(0, 12)}</span></div>
    </div>
  </div>
);

export function BaselineRow({ b, onDecide }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-t border-suite-neutral-100 align-top" data-testid={`baseline-row-${b.id}`}>
        <td className="px-3 py-2">
          <button type="button" onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 font-medium" data-testid={`baseline-toggle-${b.id}`}>
            {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}{b.employee_name}
          </button>
        </td>
        <td className="px-3 py-2">{MONTHS_IT[b.month - 1]} {b.year}</td>
        {Object.keys(LABELS).map((k) => <td key={k} className="px-3 py-2 text-right font-mono">{fmt(b.counters[k], b.counters[k].closing_unit)}</td>)}
        <td className="px-3 py-2 space-y-1">
          {b.anomalies.length === 0 ? <span className="text-suite-neutral-500">Nessuna</span> : b.anomalies.map((a) => (
            <div key={a.code} className={`rounded border px-1.5 py-0.5 ${TONE[a.severity]}`} title={a.msg}>{SEVERITY[a.severity]}: {a.msg}</div>
          ))}
        </td>
        <td className="px-3 py-2" data-testid={`baseline-status-${b.id}`}>{b.status === "approved" ? "Approvata" : "Proposta"}</td>
        <td className="px-3 py-2 text-right whitespace-nowrap">
          {b.status === "proposed" && (
            <>
              <Button size="sm" variant="ghost" className="h-7 px-2" disabled={!b.approvable} onClick={() => onDecide(b, true)} data-testid={`approve-baseline-${b.id}`}><Check size={13} className="mr-1" />Approva</Button>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => onDecide(b, false)} data-testid={`reject-baseline-${b.id}`}><X size={13} className="mr-1" />Rifiuta</Button>
            </>
          )}
        </td>
      </tr>
      {open && <tr><td colSpan={8} className="p-0"><Details b={b} /></td></tr>}
    </>
  );
}
